import { useEffect, useRef, useState } from 'react'
import { Loader2, RefreshCw, ScanLine, Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { sha256Hex } from '@/shared/lib/hash'
import { confirmUpload, presignFile, putToPresignedUrl, reserveUpload } from '@/features/geography/api'
import {
  ballotFor, commitForm, formExtraction, getForm, reExtractForm, uploadForm,
  type BallotEntry, type Figures, type OcrJob, type TallyForm,
} from '../api'
import { formLabel, seatLabel } from '../vocab'
import { UnitPicker } from '@/features/candidacy/UnitPicker'
import { ReviewGrid, type FieldState } from './ReviewGrid'

/**
 * Upload → read → review → commit.
 *
 * ## Step three is the feature and the other three are plumbing
 *
 * The review puts the extracted numbers beside the form image, makes every field editable,
 * and — the part that matters — **keeps the machine's value visible after a human edits it**.
 * Per-field confidence is shown, low confidence is highlighted, and nothing is auto-rejected:
 * confidence is the machine's opinion of its own reading, not a probability that the number
 * is right.
 *
 * ## Committing writes the disagreements
 *
 * Every field whose final value differs from what the machine proposed becomes a permanent
 * variance row. That is what makes "the OCR read 412, the agent entered 421, the supervisor
 * accepted the agent" a sentence somebody can produce two years later.
 */
export function FormWizard({
  open, formId, electionId, onClose, onDone,
}: {
  open: boolean
  formId: string | null
  electionId: string
  onClose: () => void
  onDone: () => void | Promise<void>
}) {
  const [form, setForm] = useState<TallyForm | null>(null)
  const [job, setJob] = useState<OcrJob | null>(null)
  const [ballot, setBallot] = useState<BallotEntry[]>([])
  const [fields, setFields] = useState<Record<string, FieldState>>({})
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [busy, setBusy] = useState<null | 'upload' | 'commit' | 'extract'>(null)
  const [error, setError] = useState<string | null>(null)

  // Upload-step inputs.
  const [seatType, setSeatType] = useState('PRESIDENT')
  const [formType, setFormType] = useState('FORM_34A')
  const [unitId, setUnitId] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) {
      setForm(null); setJob(null); setBallot([]); setFields({})
      setImageUrl(null); setError(null); setUnitId(null)
    }
  }, [open])

  /* Load an existing form when the page opened one, rather than starting at upload. */
  useEffect(() => {
    if (!open || !formId) return
    let live = true
    void (async () => {
      try {
        const f = await getForm(formId)
        if (live) setForm(f)
      } catch (err) {
        if (live) setError(explain(err))
      }
    })()
    return () => { live = false }
  }, [open, formId])

  /*
     Poll the EXTRACTION while it is running.

     `form?.id` and `form?.statusCode` are both in the array: a re-extraction changes the
     status back to EXTRACTING and must restart the poll, and a poll bound to the first
     status would stop watching after the first failure.
  */
  useEffect(() => {
    const id = form?.id
    if (!id || form?.statusCode === 'COMMITTED') return
    let live = true
    let timer: ReturnType<typeof setTimeout>

    const poll = async () => {
      try {
        const next = await formExtraction(id)
        if (!live) return
        setJob(next)
        if (!next || next.statusCode === 'QUEUED' || next.statusCode === 'RUNNING') {
          timer = setTimeout(() => void poll(), 2000)
        } else {
          setForm((f) => (f ? { ...f, statusCode: next.statusCode === 'SUCCEEDED'
            ? 'EXTRACTED' : 'FAILED' } : f))
        }
      } catch (err) {
        if (live) setError(explain(err))
      }
    }
    void poll()
    return () => { live = false; clearTimeout(timer) }
  }, [form?.id, form?.statusCode])

  /* The ballot and the image, once we know which race the form belongs to. */
  useEffect(() => {
    if (!form) return
    let live = true
    void (async () => {
      try {
        const [entries, presigned] = await Promise.all([
          ballotFor({ electionId, seatType: form.seatTypeCode, unitId: form.unitId }),
          // Presigned, not /download: an <img> cannot carry the bearer token.
          presignFile(form.fileId).catch(() => null),
        ])
        if (!live) return
        setBallot(entries)
        setImageUrl(presigned?.url ?? null)
      } catch (err) {
        if (live) setError(explain(err))
      }
    })()
    return () => { live = false }
  }, [form, electionId])

  /* Seed the review fields from the extraction the first time it arrives. */
  useEffect(() => {
    if (!form || job?.statusCode !== 'SUCCEEDED') return
    setFields((current) => (Object.keys(current).length > 0 ? current
      : seedFields(job, ballot)))
  }, [form, job, ballot])

  const upload = async (file: File) => {
    if (!unitId) { setError('Choose the place this form was counted at.'); return }
    setBusy('upload')
    setError(null)
    try {
      const reserved = await reserveUpload({
        filename: file.name,
        // Derived from the extension: a phone-saved scan often reports
        // application/octet-stream, which the storage purpose policy refuses outright.
        contentType: contentTypeOf(file),
        sizeBytes: file.size,
        purpose: 'FORM_34',
      })
      await putToPresignedUrl(reserved.uploadUrl, file)
      const checksum = await sha256Hex(file)
      await confirmUpload(reserved.fileId, checksum)
      setForm(await uploadForm({
        electionId, seatTypeCode: seatType, unitId, formTypeCode: formType,
        fileId: reserved.fileId, sha256: checksum, notes: file.name, extract: true,
      }))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const reExtract = async () => {
    if (!form) return
    setBusy('extract')
    setError(null)
    setJob(null)
    try {
      setForm(await reExtractForm(form.id))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const commit = async () => {
    if (!form) return
    setBusy('commit')
    setError(null)
    try {
      await commitForm(form.id, toFigures(fields), toOcrValues(fields))
      await onDone()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const reading = form != null && form.statusCode !== 'COMMITTED'
    && (job === null || job.statusCode === 'QUEUED' || job.statusCode === 'RUNNING')
  const failed = job?.statusCode === 'FAILED'
  const committed = form?.statusCode === 'COMMITTED'
  const step = !form ? 1 : reading ? 2 : committed ? 4 : 3

  return (
    <Modal open={open} onClose={onClose} width="lg"
      title={form ? `${formLabel(form.formTypeCode)} · ${form.unitName ?? ''}`
        : 'Upload a results form'}
      description="Upload, read, check every number against the image, then commit."
      footer={(
        <>
          <button type="button" className="erp-btn h-9 px-3 text-erp-sm" onClick={onClose}>
            {committed ? 'Done' : 'Cancel'}
          </button>
          {form && !committed && (failed || job?.statusCode === 'SUCCEEDED') && (
            <button type="button" disabled={busy !== null} onClick={() => void reExtract()}
              className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
              {busy === 'extract' ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5" />}
              Read it again
            </button>
          )}
          {form && !committed && !reading && (
            <button type="button" disabled={busy !== null}
              onClick={() => void commit()}
              className="erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2">
              {busy === 'commit' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Commit {changedCount(fields) > 0
                ? `with ${changedCount(fields)} change${changedCount(fields) === 1 ? '' : 's'}`
                : 'as read'}
            </button>
          )}
        </>
      )}>
      <div className="space-y-4">
        <StepRail step={step} />
        <ErrorStrip message={error} className="rounded-md border" />

        {/* ── 1. upload ─────────────────────────────────────────────────── */}
        {!form && (
          <div className="space-y-3">
            <div className="grid gap-2"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
              <label className="block">
                <span className="block text-erp-xs text-muted-foreground mb-1">Race</span>
                <select value={seatType} className="erp-input h-9 w-full text-erp-sm"
                  onChange={(e) => {
                    setSeatType(e.target.value)
                    setFormType(stationFormFor(e.target.value))
                  }}>
                  {['PRESIDENT', 'GOVERNOR', 'SENATOR', 'WOMAN_REP', 'MP', 'MCA'].map((s) => (
                    <option key={s} value={s}>{seatLabel(s)}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="block text-erp-xs text-muted-foreground mb-1">Form</span>
                <select value={formType} className="erp-input h-9 w-full text-erp-sm"
                  onChange={(e) => setFormType(e.target.value)}>
                  {FORM_TYPES.map((f) => (
                    <option key={f} value={f}>{formLabel(f)}</option>
                  ))}
                </select>
              </label>
              <div>
                <span className="block text-erp-xs text-muted-foreground mb-1">
                  Where it was counted
                </span>
                {/*
                    The picker is narrowed to the level the SEAT is contested at, so a
                    presidential form asks for a country and an MCA form for a ward — the
                    same SeatTypeRules mapping the server enforces, surfaced as a filter
                    rather than as a rejection after the fact.
                */}
                <UnitPicker level={levelForSeat(seatType)} value={unitId} onPick={setUnitId} />
              </div>
            </div>

            <div className="rounded-md border border-dashed border-border px-4 py-8 text-center">
              <ScanLine className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
              <p className="text-erp-sm">Drop a scan or photo of the form, or</p>
              <button type="button" disabled={busy !== null || !unitId}
                onClick={() => fileInput.current?.click()}
                className="erp-btn h-9 px-3 mt-2 inline-flex items-center gap-2 text-erp-sm">
                {busy === 'upload' ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Upload className="h-3.5 w-3.5" />}
                Choose a file
              </button>
              {!unitId && (
                <p className="text-erp-xs text-muted-foreground mt-2">
                  Choose the place first — a form with no station is evidence of nothing.
                </p>
              )}
              <input ref={fileInput} type="file" hidden
                accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  e.target.value = ''
                  if (f) void upload(f)
                }} />
            </div>
            <Note tone="info">
              PDFs are fine — scanners produce them by default, and the pages are rendered
              before they are read.
            </Note>
          </div>
        )}

        {/* ── 2. read ───────────────────────────────────────────────────── */}
        {form && reading && (
          <Note tone="info">
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
              <span>
                <strong>Reading the form…</strong>
                <span className="block mt-1 text-erp-xs">
                  The first read after a restart waits for the engine to warm up, so this can
                  take up to a minute. Nothing is lost if you close this — the form keeps its
                  place, and the figures can always be keyed in by hand.
                </span>
              </span>
            </span>
          </Note>
        )}

        {form && failed && (
          <Note tone="bad">
            <strong>The machine could not read this form.</strong>
            <span className="block mt-1 text-erp-xs">{job?.error}</span>
            <span className="block mt-1.5 text-erp-xs">
              The image is still here and is still the evidence. Read it again after
              re-scanning, or key the numbers in below — a form the machine cannot read is not
              a form nobody can read.
            </span>
          </Note>
        )}

        {/* ── 3. review — the point of the wizard ───────────────────────── */}
        {form && !reading && !committed && (
          <ReviewGrid
            fields={fields}
            ballot={ballot}
            imageUrl={imageUrl}
            onChange={(key, value) =>
              setFields((f) => ({ ...f, [key]: { ...f[key]!, entered: value } }))}
          />
        )}

        {committed && (
          <Note tone="good">
            <strong>Committed.</strong> A return has been created at <strong>entered</strong>.
            It needs a second person to verify it against this form before it counts towards
            any board.
          </Note>
        )}
      </div>
    </Modal>
  )
}

// ── seeding and shaping ──────────────────────────────────────────────────────────────────

const WHOLE_FORM_FIELDS = [
  { key: 'registered_voters', label: 'Registered voters' },
  { key: 'valid_votes', label: 'Valid votes' },
  { key: 'rejected_votes', label: 'Rejected ballots' },
  { key: 'spoilt_votes', label: 'Spoilt ballots' },
  { key: 'disputed_votes', label: 'Disputed ballots' },
]

const FORM_TYPES = [
  'FORM_34A', 'FORM_34B', 'FORM_34C', 'FORM_35A', 'FORM_35B', 'FORM_36A', 'FORM_36B',
  'FORM_37A', 'FORM_37B', 'FORM_38A', 'FORM_38B', 'FORM_39A', 'FORM_39B', 'OTHER',
]

/** The geography level a seat is contested at — the server's SeatTypeRules, mirrored. */
const levelForSeat = (seat: string) => ({
  PRESIDENT: 'COUNTRY', GOVERNOR: 'COUNTY', SENATOR: 'COUNTY',
  WOMAN_REP: 'COUNTY', MP: 'CONSTITUENCY', MCA: 'WARD',
}[seat] ?? null)

const stationFormFor = (seat: string) => ({
  PRESIDENT: 'FORM_34A', GOVERNOR: 'FORM_35A', SENATOR: 'FORM_36A',
  WOMAN_REP: 'FORM_37A', MP: 'FORM_38A', MCA: 'FORM_39A',
}[seat] ?? 'OTHER')

function contentTypeOf(file: File): string {
  const name = file.name.toLowerCase()
  if (name.endsWith('.pdf')) return 'application/pdf'
  if (name.endsWith('.png')) return 'image/png'
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg'
  return file.type || 'image/jpeg'
}

/**
 * Build the review fields from the extraction.
 *
 * **`proposed` is set once and never changed after this.** It is what the machine said, and
 * the whole audit story depends on it surviving whatever the human types.
 *
 * The numbers are matched positionally against the numeric tokens the engine found, which is
 * a deliberately weak guess: a real Form 34A has a layout this build does not model yet, so
 * the honest thing is to offer the machine's readings in order, show the confidence of each,
 * and let a human place them. Anything cleverer would be a guess dressed as an extraction.
 */
function seedFields(job: OcrJob | null, ballot: BallotEntry[]): Record<string, FieldState> {
  const numbers = numericTokens(job)
  const out: Record<string, FieldState> = {}
  let i = 0

  for (const c of ballot) {
    const token = numbers[i++]
    out[`candidacy:${c.id}`] = {
      label: c.ballotName,
      proposed: token?.text ?? null,
      confidence: token?.confidence ?? null,
      entered: token?.text ?? '',
      kind: 'candidate',
    }
  }
  for (const f of WHOLE_FORM_FIELDS) {
    const token = numbers[i++]
    out[f.key] = {
      label: f.label,
      proposed: token?.text ?? null,
      confidence: token?.confidence ?? null,
      entered: token?.text ?? '',
      kind: 'total',
    }
  }
  return out
}

/** Every token the engine read that looks like a vote count, in reading order. */
function numericTokens(job: OcrJob | null): { text: string; confidence: number }[] {
  if (!job?.rawResponse) return []
  try {
    const parsed = JSON.parse(job.rawResponse) as {
      pages?: { lines?: { text?: string; confidence?: number }[] }[]
    }
    return (parsed.pages ?? [])
      .flatMap((p) => p.lines ?? [])
      .map((l) => ({ text: (l.text ?? '').trim(), confidence: l.confidence ?? 0 }))
      .filter((l) => /^\d{1,7}$/.test(l.text))
  } catch {
    return []
  }
}

const num = (v: string) => {
  const n = Number.parseInt(v, 10)
  return Number.isFinite(n) && n >= 0 ? n : 0
}

function toFigures(fields: Record<string, FieldState>): Figures {
  const candidates = Object.entries(fields)
    .filter(([key]) => key.startsWith('candidacy:'))
    .map(([key, f]) => ({ candidacyId: key.slice('candidacy:'.length), votes: num(f.entered) }))
  return {
    registeredVoters: num(fields.registered_voters?.entered ?? ''),
    validVotes: num(fields.valid_votes?.entered ?? ''),
    rejectedVotes: num(fields.rejected_votes?.entered ?? ''),
    disputedVotes: num(fields.disputed_votes?.entered ?? ''),
    spoiltVotes: num(fields.spoilt_votes?.entered ?? ''),
    candidates,
  }
}

/**
 * What the MACHINE proposed, for the fields it proposed anything for.
 *
 * Sent back so the server can write a variance per disagreement. A field the machine offered
 * no reading of is omitted: a variance against a value nobody saw is not a record of a
 * decision.
 */
function toOcrValues(fields: Record<string, FieldState>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, f] of Object.entries(fields)) {
    if (f.proposed != null && f.proposed !== '') out[key] = f.proposed
  }
  return out
}

const changedCount = (fields: Record<string, FieldState>) =>
  Object.values(fields).filter(
    (f) => f.proposed != null && f.proposed !== '' && f.entered.trim() !== f.proposed).length

const STEPS = ['Upload', 'Read', 'Review', 'Committed']

function StepRail({ step }: { step: number }) {
  return (
    <ol className="flex items-center gap-1 text-erp-xs">
      {STEPS.map((label, i) => {
        const n = i + 1
        return (
          <li key={label} className={cn(
            'h-7 px-2.5 inline-flex items-center gap-1.5 rounded-md border',
            n === step ? 'border-chocolate bg-chocolate-soft/40 text-chocolate font-medium'
              : n < step ? 'border-border text-muted-foreground'
                : 'border-dashed border-border text-muted-foreground/50',
          )}>
            <span className="tabular-nums">{n}</span>{label}
          </li>
        )
      })}
    </ol>
  )
}
